import { ReactElement, useCallback } from 'react'
import { Box, Stack, TextField, Typography } from '@mui/material'
import usePlanTableEditMode from '@editor/hooks/usePlanTableEditMode'
import usePlanTableMutations from '@editor/hooks/usePlanTableMutations'
import usePlanViewPlanTables from '@editor/pages/PlanView/hooks/usePlanViewPlanTables'
import { DeleteButton } from '@ui/actions'
import { usePlanTableStore } from '@editorStores'
import { usePlanTableLayoutMetrics, useSelectedPlanElementId } from '@editorHooks'
import { isTemplatePlanTable } from 'src/domain/planTables/types'
import PlanExportConfigFields from './PlanExportConfigFields'
import PlanTableDataEditor from './PlanTableDataEditor'
import PlanTablePositionAnchorFields from './PlanTablePositionAnchorFields'
import PlanTableTemplateOptionsFields from './PlanTableTemplateOptionsFields'

const PlanTableForm = (): ReactElement => {
  const selectedId = useSelectedPlanElementId('table')
  const planTable = usePlanTableStore(state =>
    selectedId ? state.tables.find(table => table.id === selectedId) : undefined,
  )
  const { updatePlanTableFields, updatePlanTableData, deletePlanTable } = usePlanTableMutations()
  const { exitPlanTableEdit } = usePlanTableEditMode()
  const layoutMetrics = usePlanTableLayoutMetrics()
  const { getTableData } = usePlanViewPlanTables()

  const handleDeleteTable = useCallback(() => {
    if (!planTable) return
    deletePlanTable(planTable.id)
    exitPlanTableEdit()
  }, [deletePlanTable, exitPlanTableEdit, planTable])

  if (!planTable) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Keine Tabelle ausgewählt.
        </Typography>
      </Box>
    )
  }

  const isTemplateTable = planTable ? isTemplatePlanTable(planTable) : false

  return (
    <Stack spacing={1} p={1}>
      <Typography variant="h6" data-cy="plan-table-form-title">
        Tabelle bearbeiten
      </Typography>

      {isTemplateTable && (
        <Typography variant="body2" color="text.secondary" data-cy="plan-table-template-info">
          Vorlagentabelle – Inhalte werden aus Projektdaten erzeugt und sind hier schreibgeschützt.
        </Typography>
      )}

      <TextField
        label="Name"
        size="small"
        value={planTable.name}
        onChange={event => updatePlanTableFields(planTable.id, { name: event.target.value })}
        fullWidth
        data-cy="plan-table-name-input"
      />

      <PlanTablePositionAnchorFields
        position={planTable.position}
        positionAnchor={planTable.positionAnchor}
        table={getTableData(planTable)}
        layoutMetrics={layoutMetrics}
        onChange={changes => updatePlanTableFields(planTable.id, changes)}
      />

      <PlanTableDataEditor
        table={getTableData(planTable)}
        readOnly={isTemplateTable}
        onTableChange={updater => updatePlanTableData(planTable.id, updater)}
      />

      {isTemplateTable && (
        <PlanTableTemplateOptionsFields
          templateKey={planTable.templateKey}
          templateOptions={planTable.templateOptions}
          onChange={templateOptions => updatePlanTableFields(planTable.id, { templateOptions })}
        />
      )}

      <PlanExportConfigFields
        planConfig={planTable.planConfig}
        onChange={planConfig => updatePlanTableFields(planTable.id, { planConfig })}
      />

      <Stack direction="row" spacing={1}>
        <DeleteButton onClick={handleDeleteTable} size="small" data-cy="plan-table-delete-button">
          Tabelle löschen
        </DeleteButton>
      </Stack>
    </Stack>
  )
}

export default PlanTableForm
